import { Component, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MaterialModule } from 'src/app/material.module';
import { SharedModule } from '../../shared.module';
import { TablerIconsModule } from 'angular-tabler-icons';
import { EnvironmentService } from 'src/app/services/apps/environment/environment.service';
import { Company } from 'src/assets/types/interface/company.interface';
import { Environment } from 'src/assets/types/interface/environment.interface';
import { PhotoPreview } from 'src/assets/types/interface/photo-preview.interface';
import { feedback_types_enum } from 'src/assets/types/enums/feedback-types.enum';
import { CompanyService } from 'src/app/services/apps/company/company.service';
import { CarouselComponent } from '../../carousel/carousel.component';

interface EnvironmentDialogData {
  environment?: Environment;
  business_units?: Company[];
}

@Component({
  selector: 'app-environment-modal',
  templateUrl: './environment-modal.component.html',
  styleUrl: './environment-modal.component.scss',
  standalone: true,
  imports: [
    MaterialModule,
    FormsModule,
    MatInputModule,
    TablerIconsModule,
    CommonModule,
    SharedModule,
    ReactiveFormsModule,
    CarouselComponent,
  ],
})
export class EnvironmentModalComponent {
  business_units: Company[];
  selectedUnity: string = '';
  files: any;
  filesUrls: string[] = [];
  selectedFile?: File;

  environment: Environment = {
    uuid: '',
    name: '',
    details: '',
    company: {
      uuid: '',
      name: '',
    },
  };
  environmentForm!: FormGroup;

  environmentPhotos: any[] = [];
  currentImageIndex: number = 0;

  showAlert = false;
  showCancel = true;
  showConfirm = true;
  alertMessage = '';

  feedbackMessage = '';
  feedbackType = '';
  showFeedback = false;

  photos: PhotoPreview[] = [];
  deletedPhotos: string[] = [];
  selectedPhotos: File[] = [];
  isEditing: boolean = false;

  private alertResolve: ((value: boolean) => void) | null;
  @ViewChild(CarouselComponent)
  carousel!: CarouselComponent;

  constructor(
    private readonly companyService: CompanyService,
    private environmentService: EnvironmentService,
    public dialogRef: MatDialogRef<EnvironmentModalComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: EnvironmentDialogData,
  ) {
    this.environment.uuid = data.environment?.uuid ?? '';
    this.environment.company =
      data.environment?.company ?? this.environment.company;
    this.isEditing = !!this.environment.uuid;
  }

  ngOnInit() {
    this.loadEnvironment();
    this.loadCompanies();
    this.environmentForm = this.fb.group({
      name: [
        this.environment?.name ?? '',
        [Validators.required, Validators.minLength(2)],
      ],
      details: [this.environment?.details ?? '', Validators.required],
      unity: [this.environment?.company?.uuid ?? '', Validators.required],
    });
  }

  loadEnvironment() {
    if (this.environment.uuid) {
      this.environmentService.getEnvironment(this.environment.uuid).subscribe({
        next: (data) => {
          this.environment = data.body ?? this.environment;
          this.environmentForm.patchValue({
            name: this.environment.name,
            details: this.environment.details,
            unity: this.environment.company?.uuid ?? '',
          });
        },
        error: (err: HttpErrorResponse) => {
          console.error('Erro ao carregar o ambiente:', err);
          this.alertMessage = 'Erro ao carregar o ambiente. Tente novamente.';
          this.showAlert = true;
        },
      });
    }
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    this.files.push(file);
  }

  loadCompanies() {
    this.companyService.getCompanies().subscribe({
      next: (data) => {
        this.business_units = data;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Erro ao carregar as empresas:', err);
      },
    });
  }

  create() {
    const payload = this.buildPayload(true);

    this.environmentService
      .createEnvironment(this.environmentForm.value.unity, payload)
      .subscribe({
        next: (res) => {
          this.carousel.upload(res.body?.uuid);
          this.dialogRef.close(res.body);
        },
        error: (err) => this.handleError(err, 'create'),
      });
  }

  update() {
    const payload = this.buildPayload(false);

    this.environmentService
      .updateEnvironment(this.environment.uuid, payload)
      .subscribe({
        next: (res) => {
          this.carousel.upload();
          this.dialogRef.close(res.body);
        },
        error: (err) => this.handleError(err, 'update'),
      });
  }

  private buildPayload(isCreate: boolean) {
    return {
      name: this.environmentForm.value.name,
      details: this.environmentForm.value.details,
      status: isCreate ? 'active' : this.environment.status,
    };
  }

  private handleError(err: any, action: 'create' | 'update') {
    const isCreate = action === 'create';

    this.feedbackMessage =
      err?.error?.message ||
      `Erro ao ${isCreate ? 'salvar' : 'editar'} o ambiente. Tente novamente.`;

    this.feedbackType = feedback_types_enum.error;
    this.showFeedback = true;

    console.error(`Erro ao ${isCreate ? 'salvar' : 'editar'} ambiente:`, err);

    setTimeout(() => (this.showFeedback = false), 3000);
  }

  close(): void {
    this.dialogRef.close(false);
  }

  async showConfirmationAlert(message: string): Promise<boolean> {
    this.alertMessage = message;
    this.showAlert = true;
    this.showCancel = this.showConfirm = true;

    return new Promise<boolean>((resolve) => {
      this.alertResolve = resolve;
    });
  }

  onAlertClosed(confirmed: boolean) {
    this.showAlert = false;
    if (this.alertResolve) {
      this.alertResolve(confirmed);
      this.alertResolve = null;
    }
  }
}
